import { useCallback, useRef, type CSSProperties, type PointerEvent } from 'react';
import { type CropRect } from '@/types';
import { clamp } from '@/utils/format';

const MIN_CROP = 32;

type HandleId = 'move' | 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface ContentRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface CropOverlayProps {
  /** Where the video's rendered content sits inside the stage, in CSS px. */
  contentRect: ContentRect;
  /** Coded size of the source video. */
  source: { width: number; height: number };
  value: CropRect;
  onChange: (crop: CropRect) => void;
  onRemove: () => void;
}

const HANDLES: Array<{ id: HandleId; className: string; cursor: string }> = [
  { id: 'nw', className: 'top-0 left-0 -translate-x-1/2 -translate-y-1/2', cursor: 'nwse-resize' },
  { id: 'n', className: 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2', cursor: 'ns-resize' },
  { id: 'ne', className: 'top-0 right-0 translate-x-1/2 -translate-y-1/2', cursor: 'nesw-resize' },
  { id: 'e', className: 'top-1/2 right-0 translate-x-1/2 -translate-y-1/2', cursor: 'ew-resize' },
  {
    id: 'se',
    className: 'bottom-0 right-0 translate-x-1/2 translate-y-1/2',
    cursor: 'nwse-resize',
  },
  { id: 's', className: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2', cursor: 'ns-resize' },
  {
    id: 'sw',
    className: 'bottom-0 left-0 -translate-x-1/2 translate-y-1/2',
    cursor: 'nesw-resize',
  },
  { id: 'w', className: 'top-1/2 left-0 -translate-x-1/2 -translate-y-1/2', cursor: 'ew-resize' },
];

function applyDrag(
  handle: HandleId,
  start: CropRect,
  dx: number,
  dy: number,
  source: { width: number; height: number },
): CropRect {
  let { x, y, width, height } = start;
  const right = start.x + start.width;
  const bottom = start.y + start.height;

  if (handle === 'move') {
    x = clamp(start.x + dx, 0, source.width - start.width);
    y = clamp(start.y + dy, 0, source.height - start.height);
  } else {
    if (handle.includes('e')) width = clamp(start.width + dx, MIN_CROP, source.width - start.x);
    if (handle.includes('s')) height = clamp(start.height + dy, MIN_CROP, source.height - start.y);
    if (handle.includes('w')) {
      x = clamp(start.x + dx, 0, right - MIN_CROP);
      width = right - x;
    }
    if (handle.includes('n')) {
      y = clamp(start.y + dy, 0, bottom - MIN_CROP);
      height = bottom - y;
    }
  }
  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(width),
    height: Math.round(height),
  };
}

/**
 * Draggable/resizable crop rectangle mapped between display pixels and
 * source pixels. Fully keyboard-operable: arrows move, Shift+arrows resize,
 * Alt makes both fine-grained, Delete removes the crop.
 */
export function CropOverlay({ contentRect, source, value, onChange, onRemove }: CropOverlayProps) {
  const drag = useRef<{
    handle: HandleId;
    startRect: CropRect;
    startX: number;
    startY: number;
  } | null>(null);
  const scale = contentRect.width / source.width;

  const beginDrag = useCallback(
    (handle: HandleId) => (event: PointerEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();
      drag.current = { handle, startRect: value, startX: event.clientX, startY: event.clientY };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [value],
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const state = drag.current;
      if (!state) return;
      const dx = (event.clientX - state.startX) / scale;
      const dy = (event.clientY - state.startY) / scale;
      onChange(applyDrag(state.handle, state.startRect, dx, dy, source));
    },
    [onChange, scale, source],
  );

  const endDrag = useCallback((event: PointerEvent<HTMLDivElement>) => {
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.altKey ? 1 : 8;
    const resize = event.shiftKey;
    let handled = true;
    let dx = 0;
    let dy = 0;
    switch (event.key) {
      case 'ArrowLeft':
        dx = -step;
        break;
      case 'ArrowRight':
        dx = step;
        break;
      case 'ArrowUp':
        dy = -step;
        break;
      case 'ArrowDown':
        dy = step;
        break;
      case 'Delete':
      case 'Backspace':
        onRemove();
        return;
      default:
        handled = false;
    }
    if (!handled) return;
    event.preventDefault();
    onChange(applyDrag(resize ? 'se' : 'move', value, dx, dy, source));
  };

  const boxStyle: CSSProperties = {
    left: contentRect.left + value.x * scale,
    top: contentRect.top + value.y * scale,
    width: value.width * scale,
    height: value.height * scale,
  };

  return (
    <div
      role="group"
      aria-label={`Crop area, ${value.width} by ${value.height} pixels. Arrow keys move, Shift resizes, Alt is fine-grained, Delete removes the crop.`}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onPointerDown={beginDrag('move')}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      style={boxStyle}
      className="absolute z-10 cursor-move touch-none border border-signal-400 shadow-[0_0_0_9999px_rgb(10_12_16/0.62)]"
    >
      {/* Rule-of-thirds guides */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-1/3 w-px bg-white/25"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-2/3 w-px bg-white/25"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-1/3 h-px bg-white/25"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-2/3 h-px bg-white/25"
      />

      <span className="absolute top-1 left-1 rounded bg-ink-950/80 px-1.5 py-0.5 font-mono text-[10px] text-signal-300">
        {value.width}×{value.height}
      </span>

      {HANDLES.map((handle) => (
        <div
          key={handle.id}
          onPointerDown={beginDrag(handle.id)}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          style={{ cursor: handle.cursor }}
          className={`absolute size-2.5 touch-none rounded-[2px] bg-white ring-1 ring-ink-900 ${handle.className}`}
        />
      ))}
    </div>
  );
}
